import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import ServiceAgreementFormDialog from "@/components/ServiceAgreementFormDialog";
import {
  FileText,
  Plus,
  Search,
  Edit3,
  Cog,
  Calendar,
  DollarSign,
  RefreshCw,
} from "lucide-react";

// Colour tokens for the renewal chip — mirrors backend expiry levels.
// Convert a frequency-tagged cost into an annualised amount.
// "annual" → x1, "quarterly" → x4, "monthly" → x12, "one-off" → treated as full amount.
const FREQ_TO_ANNUAL = { annual: 1, quarterly: 4, monthly: 12, "one-off": 1 };

// Build the per-machine cost line, e.g. "$2,900 / machine / year".
const perMachineCostLine = (a) => {
  const cost = Number(a.cost);
  const n = a.machines?.length || 0;
  if (!cost || n <= 0) return null;
  const freq = a.payment_frequency || "annual";
  const machineWord = n === 1 ? "machine" : "machines";
  if (freq === "one-off") {
    const per = cost / n;
    return {
      main: `$${per.toLocaleString(undefined, { maximumFractionDigits: 0 })} / machine`,
      note: `one-off · $${cost.toLocaleString(undefined, { maximumFractionDigits: 0 })} ÷ ${n} ${machineWord}`,
    };
  }
  const annual = cost * (FREQ_TO_ANNUAL[freq] || 1);
  const perYear = annual / n;
  return {
    main: `$${perYear.toLocaleString(undefined, { maximumFractionDigits: 0 })} / machine / year`,
    note: freq === "annual"
      ? `$${cost.toLocaleString(undefined, { maximumFractionDigits: 0 })} ÷ ${n} ${machineWord}`
      : `${freq} × ${FREQ_TO_ANNUAL[freq]} ÷ ${n} ${machineWord}`,
  };
};

const LEVEL_CHIP = {
  ok: { bg: "#DFF7E4", fg: "#0B6B2A", label: "OK" },
  watch: { bg: "#FFF3D4", fg: "#7A5A00", label: "Renews soon" },
  warn: { bg: "#FFE1CC", fg: "#7A3E00", label: "Renew now" },
  urgent: { bg: "#FCC8CE", fg: "#8A1F1F", label: "Urgent" },
  expired: { bg: "#E5263F", fg: "#fff", label: "Expired" },
  unknown: { bg: "#F0F0F0", fg: "#607087", label: "No end date" },
};

const ServiceAgreements = () => {
  const { isManager } = useAuth();
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [machines, setMachines] = useState([]);
  const [search, setSearch] = useState("");
  const [levelFilter, setLevelFilter] = useState("all");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const [a, s, m] = await Promise.all([
        api.get("/service-agreements"),
        api.get("/suppliers"),
        api.get("/machines"),
      ]);
      setItems(a.data || []);
      setSuppliers(s.data || []);
      setMachines(m.data || []);
    } catch {
      toast.error("Could not load agreements");
    }
  };
  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((a) => {
      if (levelFilter !== "all" && a.expiry_status?.level !== levelFilter)
        return false;
      if (!q) return true;
      return (
        a.name.toLowerCase().includes(q) ||
        (a.supplier_name || "").toLowerCase().includes(q) ||
        (a.machines || []).some((m) => (m.name || "").toLowerCase().includes(q))
      );
    });
  }, [items, search, levelFilter]);

  const counts = useMemo(() => {
    const c = { all: items.length };
    items.forEach((a) => {
      const l = a.expiry_status?.level;
      if (!l) return;
      c[l] = (c[l] || 0) + 1;
    });
    return c;
  }, [items]);

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="service-agreements-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="label-eyebrow flex items-center gap-2">
            <FileText size={14} strokeWidth={2.5} />
            Machinery service contracts · renewals · inclusions
          </div>
          <h1 className="font-display text-5xl leading-none mt-2">
            Service Agreements
          </h1>
        </div>
        {isManager && (
          <button
            data-testid="agreement-add-btn"
            onClick={() => {
              setEditing(null);
              setFormOpen(true);
            }}
            className="nb-btn nb-btn-primary px-5 py-4 flex items-center gap-2"
          >
            <Plus size={20} strokeWidth={2.5} /> New agreement
          </button>
        )}
      </div>

      {/* Renewal-level chip strip */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="agreement-level-chips">
        {[
          ["all", "All"],
          ["urgent", "Urgent"],
          ["warn", "Renew now"],
          ["watch", "Soon"],
          ["ok", "OK"],
          ["expired", "Expired"],
        ].map(([lv, lbl]) => (
          <button
            key={lv}
            data-testid={`agreement-level-chip-${lv}`}
            onClick={() => setLevelFilter(lv)}
            className={`nb-btn px-3 py-1.5 text-sm ${
              levelFilter === lv ? "nb-btn-primary" : "bg-white"
            }`}
          >
            {lbl}{" "}
            <span className="text-[10px] text-[#607087] ml-1">
              ({counts[lv] ?? 0})
            </span>
          </button>
        ))}
      </div>

      <div className="relative mb-6">
        <Search
          size={20}
          strokeWidth={2.5}
          className="absolute left-3.5 top-1/2 -translate-y-1/2"
        />
        <input
          data-testid="agreement-search"
          className="nb-input pl-12 h-14 text-lg"
          placeholder="Search by name, supplier, machine…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((a) => {
          const lvl = a.expiry_status?.level || "unknown";
          const chip = LEVEL_CHIP[lvl] || LEVEL_CHIP.unknown;
          const days = a.expiry_status?.days_remaining;
          return (
            <div
              key={a.id}
              className="nb-card p-5 flex flex-col gap-2"
              data-testid={`agreement-card-${a.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-display text-xl truncate">{a.name}</div>
                  {a.supplier_name && (
                    <div className="text-xs font-bold text-[#228EBF] truncate">
                      {a.supplier_name}
                    </div>
                  )}
                </div>
                {isManager && (
                  <button
                    data-testid={`agreement-edit-${a.id}`}
                    onClick={() => {
                      setEditing(a);
                      setFormOpen(true);
                    }}
                    className="nb-btn bg-white p-2"
                    aria-label="edit"
                  >
                    <Edit3 size={14} strokeWidth={2.5} />
                  </button>
                )}
              </div>

              <div
                className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-bold w-max"
                style={{ background: chip.bg, color: chip.fg }}
                data-testid={`agreement-chip-${a.id}`}
              >
                {chip.label}
                {days != null && (
                  <span>
                    ·{" "}
                    {days >= 0
                      ? `${days}d left`
                      : `${Math.abs(days)}d overdue`}
                  </span>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs mt-1">
                <div>
                  <div className="label-eyebrow text-[#607087] flex items-center gap-1">
                    <Calendar size={10} strokeWidth={2.5} /> Period
                  </div>
                  <div>
                    {a.period_start ? a.period_start.slice(0, 10) : "—"} →{" "}
                    {a.period_end ? a.period_end.slice(0, 10) : "—"}
                  </div>
                </div>
                <div>
                  <div className="label-eyebrow text-[#607087] flex items-center gap-1">
                    <DollarSign size={10} strokeWidth={2.5} /> Cost
                  </div>
                  <div>
                    {a.cost != null
                      ? `$${Number(a.cost).toLocaleString(undefined, { minimumFractionDigits: 2 })}`
                      : "—"}
                    <span className="text-[#607087] ml-1">
                      / {a.payment_frequency}
                    </span>
                  </div>
                </div>
              </div>

              {a.auto_renew && (
                <div className="text-[11px] text-[#0B6B2A] font-bold flex items-center gap-1">
                  <RefreshCw size={11} strokeWidth={2.5} /> Auto-renew
                </div>
              )}

              {(() => {
                const pm = perMachineCostLine(a);
                if (!pm) return null;
                return (
                  <div
                    data-testid={`agreement-per-machine-${a.id}`}
                    className="mt-1 px-2 py-1.5 rounded border-2 border-black bg-[#FFF7D6] flex flex-col leading-tight"
                  >
                    <div className="text-[13px] font-bold text-black">
                      ≈ {pm.main}
                    </div>
                    <div className="text-[10px] text-[#607087]">{pm.note}</div>
                  </div>
                );
              })()}

              {a.machines?.length > 0 && (
                <div className="text-xs">
                  <div className="label-eyebrow text-[#607087] mb-0.5 flex items-center gap-1">
                    <Cog size={11} strokeWidth={2.5} /> Covers {a.machines.length}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {a.machines.slice(0, 6).map((m) => (
                      <span
                        key={m.id}
                        data-testid={`agreement-covers-${m.id}`}
                        className="text-[10px] px-1.5 py-0.5 border-2 border-black rounded bg-white"
                      >
                        {m.name}
                      </span>
                    ))}
                    {a.machines.length > 6 && (
                      <span className="text-[10px] text-[#607087]">
                        +{a.machines.length - 6} more
                      </span>
                    )}
                  </div>
                </div>
              )}

              {a.inclusions?.length > 0 && (
                <div className="border-t border-black/10 pt-2 mt-1">
                  <div className="label-eyebrow text-[#607087] mb-1">
                    Inclusions
                  </div>
                  <ul className="text-[11px] leading-snug space-y-0.5">
                    {a.inclusions.slice(0, 4).map((s) => (
                      <li key={s} className="flex items-start gap-1">
                        <span className="text-[#0B6B2A] mt-0.5">✓</span>
                        <span>{s}</span>
                      </li>
                    ))}
                    {a.inclusions.length > 4 && (
                      <li className="text-[#607087]">
                        +{a.inclusions.length - 4} more…
                      </li>
                    )}
                  </ul>
                </div>
              )}

              {a.document_paths?.length > 0 && (
                <div className="text-[11px] text-[#607087] flex items-center gap-1">
                  <FileText size={11} strokeWidth={2.5} />{" "}
                  {a.document_paths.length} document
                  {a.document_paths.length === 1 ? "" : "s"}
                </div>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="col-span-full nb-card p-10 text-center">
            <div className="font-display text-2xl">
              {items.length === 0 ? "No agreements yet" : "No matches"}
            </div>
            <div className="text-sm text-[#607087] mt-1">
              {items.length === 0
                ? "Log your first service agreement to track renewals + inclusions."
                : "Try a different filter or clear the search."}
            </div>
          </div>
        )}
      </div>

      <ServiceAgreementFormDialog
        open={formOpen}
        initial={editing}
        suppliers={suppliers}
        machines={machines}
        onClose={() => setFormOpen(false)}
        onSaved={load}
        onDelete={load}
      />
    </div>
  );
};

export default ServiceAgreements;
